import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState
} from '@ngrx/signals';

import { ACCESS_STORE } from '@core-interactions/stores/access.store';
import { storeToLocalStorage } from '@shared/utils/local-storage';
import { Account } from '@stores/models/app/account';
import { USER_STORE } from '@stores/user.store';

export interface AppState {
  selectedClientId: string;
  clientSetupLoading: boolean;
  clientSetupLoaded: boolean;
  selectedReportId: string | null;
  selectedAccountId: string;
}

export const INITIAL_STATE: AppState = {
  selectedClientId: '',
  clientSetupLoading: false,
  clientSetupLoaded: false,
  selectedReportId: null,
  selectedAccountId: ''
};

type SelectedAccount = Account | null;

export const APP_STORE = signalStore(
  withState<AppState>(INITIAL_STATE),
  withComputed((store, userStore = inject(USER_STORE)) => ({
    selectedAccount: computed<SelectedAccount>(() => {
      const selectedAccountId = store.selectedAccountId();
      const user = userStore.data();

      if (!user || !selectedAccountId) {
        return null;
      }

      return user.accounts.find(a => a.id === selectedAccountId) ?? null;
    })
  })),
  withMethods((store, accessStore = inject(ACCESS_STORE)) => ({
    selectClient(clientId: string): void {
      patchState(store, {
        selectedClientId: clientId,
        clientSetupLoading: true,
        clientSetupLoaded: false
      });

      storeToLocalStorage('lastSelectedClientId', clientId);
    },

    setupClientSuccess(clientId: string): void {
      patchState(store, {
        clientSetupLoading: false,
        clientSetupLoaded: !!clientId
      });
    },

    setupClientFailure(): void {
      patchState(store, {
        clientSetupLoading: false,
        clientSetupLoaded: false
      });
    },

    selectAccount(accountId: string, clientId: string): void {
      patchState(store, {
        selectedClientId: clientId,
        selectedAccountId: accountId,
        clientSetupLoading: true,
        clientSetupLoaded: false
      });

      clientId
        ? storeToLocalStorage('lastSelectedClientId', clientId)
        : localStorage.removeItem('lastSelectedClientId');

      accessStore.resetReports();
    },

    selectReport(reportId: string): void {
      patchState(store, { selectedReportId: reportId });

      storeToLocalStorage('lastSelectedReportId', reportId);
    }
  }))
);
